import { AbstractControl, ValidatorFn } from '@angular/forms'

/**
 * Currently isValidCpt is loosely checking CPT validity and as it connects 5 modules won't be updating it.
 * As it increases the scope of PIA-3213
 */
export function isValidCPT(code: string) {
  return (
    typeof code === 'string' &&
    code.length === 5 &&
    /^[a-zA-Z0-9]\d\d\d[a-zA-Z0-9]$/.test(code)
  )
}

/**
 * Determines if given code is a valid Healthcare Common Procedure Code
 *
 * HCPC codes always:
 * - 5 characters
 * - start with a letter
 * - other four characters are always numbers
 */
export function isValidHCPC(code: string) {
  return typeof code === 'string' && code.length === 5 && /^[a-zA-Z]\d\d\d\d$/.test(code)
}

export function isValidProcedureCode(code: string) {
  // The strict CPT rule is inlined rather than delegated to `isValidCPT` above, which is
  // deliberately looser and shared with callers this must not change. Keep it in step with the
  // API's `isValidProcedureCode`, so the dialog refuses exactly what the route refuses.
  const isStrictCPT = typeof code === 'string' && code.length === 5 && /^\d\d\d\d[a-zA-Z0-9]$/.test(code)

  return isStrictCPT || isValidHCPC(code)
}

export function standardizeProcedureCode(
  code: string,
): [string | undefined, string | undefined] {
  let standardizedCode = code.toUpperCase()
  let modifier = ''

  // if the first five characters are numbers, then assume it is a CPT code
  if (/^[A-Z0-9]\d\d\d[A-Z0-9]$/.test(standardizedCode.substr(0, 5))) {
    standardizedCode = standardizedCode.substr(0, 5)
    modifier = code.toUpperCase().substr(5)
  }

  // if we have an invalid CPT/HCPCS code, return nothing
  if (!isValidCPT(standardizedCode)) {
    return [undefined, undefined]
  }

  // transform commonly misused modifiers
  switch (modifier) {
    case 'LEFT':
      modifier = 'LT'
      break
    case 'RIGHT':
      modifier = 'RT'
      break
    case 'LTRT':
      modifier = '50'
      break // bilateral modifier 50
    case 'RTLT':
      modifier = '50'
      break // bilateral modifier 50
  }

  // get rid of completely invalid modifiers
  if (modifier.length !== 2) {
    modifier = ''
  }

  return [standardizedCode, modifier.length > 0 ? modifier : undefined]
}

export const procedureCodeValidator: ValidatorFn = (
  control: AbstractControl,
): { [key: string]: any } | null => {
  if (Array.isArray(control.value)) {
    return control.value.every((code) => isValidCPT(code)) ? null : { procedureCode: { value: control.value } }
  }

  const valid = isValidCPT(control.value)
  return valid ? null : { procedureCode: { value: control.value } }
}
